import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { toggleSidebar } from "../../../../store/sessionSlice";

import {Layout, Button, Avatar, Typography} from 'antd';
import {
  LaptopOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  DashboardOutlined,
  AppleOutlined
} from '@ant-design/icons';
import './style.scss';
import stickerMakerUrls from "../../../../configs/sticker_maker/stickerMakerUrls";
import robloxUrls from "../../../../configs/roblox/robloxUrls";
import modsfactoryUrls from "../../../../configs/mods_factory/modsfactoryUrls";
import { fetchApps } from "../../../../actions/community/appsActions";
import { selectAppEntities, selectAppIds } from "../../../../store/community/appsSlice";
import appMakerUrls from "../../../../configs/community/communityUrls";
import watcherUrls from "../../../../configs/watcher/watcherUrls";
import OptionalLink from "../../../../componets/OptionalLink";

const { Sider } = Layout;
const { Text } = Typography;

export interface LinkInterface {
  url: string;
  thumbnailUrl?: string | undefined | null;
  name: string;
  thumbnail?: string;
  icon?: JSX.Element;
  active?: Function;
}

export interface ItemInterface {
  Icon: JSX.Element;
  title: string;
  description: string;
  links: LinkInterface[];
}

const Sidebar: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { pathname } = location;
  const appIds = useAppSelector(selectAppIds);
  const apps = useAppSelector(selectAppEntities);

  const [activePane, setActivePane] = useState<number | null>(null);
  const { collapsed } = useAppSelector(state => state.session);

  const items: ItemInterface[] = [
    {
      Icon: <LaptopOutlined />,
      title: 'Dashboard',
      description: 'Choose between layouts to experience different look for your projects.',
      links: [
        {
          name: 'Web Analytics',
          url: '/',
          icon: <DashboardOutlined />
        },
        {
          name: 'Community Apps',
          url: appMakerUrls.appsPath(),
          icon: <AppleOutlined />
        },
        {
          name: 'Bundles',
          url: watcherUrls.bundlesPath(),
          icon: <AppleOutlined />
        }
      ]
    },
    {
      Icon: <AppstoreOutlined />,
      title: 'Applications',
      description: 'Administration for currents projects.',
      links: [
        // {
        //   name: 'CartoonDraw',
        //   url: '/cartoondraw'
        // },
        {
          name: 'CraftyCraft',
          url: '/crafty_craft/addons',
          thumbnail: '/crafty_craft.png'
        },
        {
          name: 'ModsCraft',
          url: '/mods_craft/addons',
          thumbnail: '/mods_craft.png'
        },
        {
          name: 'CallRecorder',
          url: '/callrecorder/bundles',
        },
        {
          name: 'StickerMaker',
          url: stickerMakerUrls.communityPath(),
          thumbnail: '/sticker_maker.png'
        },
        {
          name: 'Roblox',
          url: robloxUrls.communityPath(),
          thumbnail: '/roblox.png'
        },
        {
          name: 'ModsFactory',
          url: modsfactoryUrls.skinsPath(),
          thumbnail: '/mods_factory.png'
        },
      ]
    }
  ];

  if (appIds.length) {
    [...appIds].sort().forEach((id) => {
      const app = apps[id];
      const url = `/community/${id}/community`;

      if (!app) {
        return;
      }

      items[1].links.push({
        name: app.bundle_name || app.name,
        thumbnail: app.bundle_icon_url || app.icon_url,
        thumbnailUrl: app.bundle_store_url,
        url: url,
        active: () => {
          const currentPath = pathname.split('/').slice(1, 3).join('/');
          const path = url.split('/').slice(1, 3).join('/');

          return currentPath === path;
        }
      });
    });
  }

  useEffect(() => {
    dispatch(fetchApps());
  }, [dispatch]);

  useEffect(() => {
    findActivePane();
  }, [apps, pathname]);

  const findActivePane = () => {
    items.forEach((item, index) => {
      if (isActivePane(item.links)) {
        return setActivePane(index);
      }
    });
  }

  const isActive = (url?: string) => {
    const prefix = url?.split('/')[1] || '404';
    return pathname.split('/')[1] === prefix;
  }

  const isActivePane = (links: LinkInterface[]) => {
    return links.filter(({ url }) => isActive(url)).length > 0;
  }

  const onClickIcon = (key: number) => {
    setActivePane(key);
    dispatch(toggleSidebar(false));
  }

  const AppIcon = ({ thumbnail, icon }: { thumbnail: string | undefined, icon?: JSX.Element }) => {
    return <Avatar
      src={thumbnail}
      alt='App icon'
      size={24}
      icon={icon || <AppstoreOutlined />}
      shape='square'
    />
  }

  const renderIcon = function(item: ItemInterface, key: number) {
    const { Icon } = item;

    return (
      <Button
        className={"v-icon" + (activePane === key ? " active" : "")}
        type='text' onClick={() => onClickIcon(key)}
        key={key}
      >
        {Icon}
      </Button>
    )
  };

  const renderName = (name: string) => (
    <Text
      style={{ maxWidth: 160 }}
      ellipsis={{ tooltip: name }}
    >
      {name}
    </Text>
  )

  const renderPane = function(item: ItemInterface, key: number) {
    const { title, description, links } = item;

    return (
      <div
        className={"v-pane" + (activePane === key ? " active" : "")}
        key={key}
      >
        <h6 className="o-title">{title}</h6>
        <small className="v-text">{description}</small>

        <nav className="v-nav">
          {
            links.map((link: LinkInterface, key) => {
              const {
                url,
                thumbnail,
                thumbnailUrl,
                icon,
                name,
                active
              } = link;
              const activeFn = active || isActive;
              let className = 'v-link';

              if (activeFn(url)) {
                className += ' active';
              }

              return (
                <span className='o-item' key={key}>
                  <OptionalLink link={thumbnailUrl || url} className='v-mr-10 v-link'>
                    {<AppIcon thumbnail={thumbnail} icon={icon} />}
                  </OptionalLink>

                  <Link to={url} className={className}>
                    {renderName(name)}
                  </Link>
                </span>
              )
            })
          }
        </nav>
      </div>
    )
  }

  return (
    <Sider
      className='v-sidebar'
      trigger={null}
      collapsible
      collapsed={collapsed}
      width='294'
    >
      <div className='v-logo'>
        <Link to='/' className='v-icon'>
          <BarChartOutlined />
        </Link>

        <Link to='/' className='v-link'>one<span>s</span>ky</Link>
      </div>

      <div className='v-nav'>
        <div className='v-icons'>{items.map(renderIcon)}</div>
        <div className='v-panes'>{items.map(renderPane)}</div>
      </div>
    </Sider>
  )
}

export default Sidebar;
